import {
  Button,
  HStack,
  Icon,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Progress,
  Text,
  VStack,
} from "@chakra-ui/react";
import { useSessionTimeout } from "app/providers/session-timeout-provider";
import type React from "react";
import { useEffect, useState } from "react";
import { IoWarningOutline } from "react-icons/io5";

interface SessionWarningModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const SessionWarningModal: React.FC<SessionWarningModalProps> = ({
  isOpen,
  onClose,
}) => {
  const { timeRemaining, extendSession, endSession } = useSessionTimeout();
  const [countdown, setCountdown] = useState(0);

  useEffect(() => {
    if (isOpen) {
      setCountdown(Math.floor(timeRemaining / 1000));

      const interval = setInterval(() => {
        const remaining = Math.floor(timeRemaining / 1000);
        setCountdown(remaining);

        if (remaining <= 0) {
          clearInterval(interval);
        }
      }, 1000);

      return () => clearInterval(interval);
    }
  }, [isOpen, timeRemaining]);

  const handleExtendSession = () => {
    extendSession();
    onClose();
  };

  const handleLogout = () => {
    endSession();
    onClose();
  };

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const progressValue = Math.max(0, (countdown / 300) * 100); // 5 minutes = 300 seconds

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => {}} // Prevent closing by clicking outside
      closeOnOverlayClick={false}
      closeOnEsc={false}
      size="md"
    >
      <ModalOverlay bg="blackAlpha.600" />
      <ModalContent>
        <ModalHeader>
          <HStack spacing={3}>
            <Icon as={IoWarningOutline} color="orange.500" boxSize={6} />
            <Text>Session Expiring</Text>
          </HStack>
        </ModalHeader>

        <ModalBody>
          <VStack spacing={4} align="stretch">
            <Text color="gray.600">
              Your session will expire due to inactivity. You will be
              automatically logged out to protect your account.
            </Text>

            <VStack spacing={2}>
              <Text fontSize="lg" fontWeight="bold" color="orange.500">
                Time remaining: {formatTime(countdown)}
              </Text>
              <Progress
                value={progressValue}
                colorScheme="orange"
                size="lg"
                width="100%"
                borderRadius="md"
              />
            </VStack>

            <Text fontSize="sm" color="gray.500">
              Click "Stay Logged In" to extend your session, or "Logout" to end
              your session now.
            </Text>
          </VStack>
        </ModalBody>

        <ModalFooter>
          <HStack spacing={3} width="100%">
            <Button variant="outline" onClick={handleLogout} size="md" flex={1}>
              Logout
            </Button>
            <Button
              colorScheme="blue"
              onClick={handleExtendSession}
              size="md"
              flex={1}
            >
              Stay Logged In
            </Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default SessionWarningModal;
